import { useCallback } from "react";
import { messageService } from "../services";
import { type Message } from "../component/types";
import type { AIChatState } from "ee/reducers/uiReducers/aiChatReducer";
import { isApiResponseError } from "../helpers";

interface UseThreadManagementProps {
  state: AIChatState;
  updateState: (updates: Partial<AIChatState>) => void;
  widgetId: string;
  queryId: string;
  logError: (errorMessage: string) => void;
  getInitialAssistantMessage: (
    initialMessage?: string,
    promptSuggestions?: Array<{ label: string; value: string }>,
  ) => Message[];
  getErrorAssistantMessage: (message: string) => Message;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  initialAssistantMessage?: string;
  initialAssistantSuggestions?: Array<{ label: string; value: string }>;
}

export function useThreadManagement({
  getErrorAssistantMessage,
  getInitialAssistantMessage,
  initialAssistantMessage,
  initialAssistantSuggestions,
  logError,
  queryId,
  setMessages,
  state,
  updateState,
  widgetId,
}: UseThreadManagementProps) {
  const getMessages = useCallback(
    async (threadId: string) => {
      try {
        const fetchedMessages = await messageService.fetchMessages({
          threadId,
          queryId,
          widgetId,
        });

        setMessages([
          ...getInitialAssistantMessage(
            initialAssistantMessage,
            initialAssistantSuggestions,
          ),
          ...fetchedMessages.reverse(),
        ]);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";

        logError(errorMessage);

        setMessages([getErrorAssistantMessage(errorMessage)]);
      } finally {
        updateState({ isThreadLoading: false });
      }
    },
    [
      queryId,
      widgetId,
      updateState,
      setMessages,
      getInitialAssistantMessage,
      initialAssistantMessage,
      initialAssistantSuggestions,
      logError,
      getErrorAssistantMessage,
    ],
  );

  const getThreads = useCallback(async () => {
    updateState({ isThreadLoading: true });

    try {
      const threads = await messageService.fetchThreads({
        queryId,
        widgetId,
      });

      if (threads.length === 0) {
        setMessages(
          getInitialAssistantMessage(
            initialAssistantMessage,
            initialAssistantSuggestions,
          ),
        );
        updateState({ isThreadLoading: false });

        return;
      }

      // BE sends the latest updated thread at the top of the array
      updateState({ threadId: threads[0].id });
      getMessages(threads[0].id);
    } catch (error) {
      const errorMessage = isApiResponseError(error)
        ? error.message
        : "Unknown error";

      logError(errorMessage);

      setMessages([getErrorAssistantMessage(errorMessage)]);
      updateState({ isThreadLoading: false });
    }
  }, [
    updateState,
    queryId,
    widgetId,
    getMessages,
    setMessages,
    getInitialAssistantMessage,
    initialAssistantMessage,
    initialAssistantSuggestions,
    logError,
    getErrorAssistantMessage,
  ]);

  const createNewThread = useCallback(async () => {
    if (!state.threadId) {
      setMessages(
        getInitialAssistantMessage(
          initialAssistantMessage,
          initialAssistantSuggestions,
        ),
      );

      return;
    }

    updateState({ isThreadLoading: true });

    try {
      const threadId = await messageService.createThread({
        widgetId,
        queryId,
      });

      updateState({ threadId });
      setMessages(
        getInitialAssistantMessage(
          initialAssistantMessage,
          initialAssistantSuggestions,
        ),
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      logError(errorMessage);

      setMessages([getErrorAssistantMessage(errorMessage)]);
    } finally {
      updateState({ isThreadLoading: false });
    }
  }, [
    state.threadId,
    updateState,
    setMessages,
    getInitialAssistantMessage,
    initialAssistantMessage,
    initialAssistantSuggestions,
    widgetId,
    queryId,
    logError,
    getErrorAssistantMessage,
  ]);

  return {
    getThreads,
    getMessages,
    createNewThread,
  };
}
